import { showApiError, showSuccess } from "@/lib/toast";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { addUserSubAdmin } from "@/sideEffects/api";
import { useAppNavigate } from "@/hooks/useAppNavigate";
import { ROUTES } from "@/constants/routeConstants";
import { useBreadcrumbs } from "@/hooks/useBreadcrumbs";
import { BaseFormLayout } from "@/pages/shared/BaseFormLayout";
import { useSearchParams } from "react-router";
import { FormSchemaType, UserSubAdminMetaForm } from "./UserSubAdminMetaForm";

export const AddNewUserSubAdminPage = () => {
  const { t } = useTranslation();
  const [queryParams] = useSearchParams();
  const firmGuid = String(queryParams.get("firmGuid"));

  useBreadcrumbs([
    {
      name: t("entities.firm"),
      path: ROUTES.FIRM,
    },
    {
      name: t("common.addNewEntity", { entity: t("entities.userSubAdmin") }),
      path: ROUTES.USER_SUB_ADMIN_ADD,
    },
  ]);

  const { goToUserSubAdminDetail } = useAppNavigate();
  const queryClient = useQueryClient();
  const addUserMutation = useMutation({
    mutationFn: (values: FormSchemaType) =>
      addUserSubAdmin({
        firmGuid: firmGuid,
        name: values.name,
        surname: values.surname,
        email: values.email,
        gender: values.gender,
        mobile: values.mobile,
      }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["getUser"],
      });
      showSuccess(t("common.addedSuccess"));
      goToUserSubAdminDetail(data.data.userId, firmGuid);
    },
    onError: showApiError,
  });

  return (
    <BaseFormLayout title={t("common.addNewEntity", { entity: t("entities.userSubAdmin") })}>
      <UserSubAdminMetaForm
        buttonText={t("common.add")}
        onSubmit={(values) => addUserMutation.mutate(values)}
        isLoading={addUserMutation.isPending}
      />
    </BaseFormLayout>
  );
};
